import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import { Role } from 'auth';
import { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import NotificationsFilters from '@/modules/issuer-dashboard/components/NotificationsFilters.tsx';
import IssuerNotificationItem from '@/modules/issuer-dashboard/components/items/IssuerNotificationItem.tsx';
import {
  useIssuerMakeIsReadNotificationsMutation,
  useIssuerNotificationsListQuery,
} from '@/modules/issuer-dashboard/queries/event-logs.ts';
import {
  Button,
  ConfirmModal,
  ConfirmModalProps,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  Spinner,
} from 'ui';
import { SelectPaginateOption } from 'ui';
import { CheckIcon, FiltersIcon } from 'ui/icons';
import { EventLogType } from 'api';

const NotificationsPage = () => {
  const { openModal } = useContext(ModalProviderContext);
  const [isReadFilter, setIsReadFilter] = useState<boolean>();
  const [typeFilter, setTypeFilter] = useState<EventLogType>();
  const [dateFilter, setDateFilter] = useState<[string, string]>();
  const [createdByFilter, setCreatedByFilter] = useState<SelectPaginateOption<number> | null>(null);
  const [params] = useSearchParams();

  const isSearchUsed = !!(isReadFilter || typeFilter || dateFilter || createdByFilter);

  const { data, isFetchingNextPage, isLoading, fetchNextPage, hasNextPage } =
    useIssuerNotificationsListQuery(
      {
        page: 1,
        page_size: 15,
        types: typeFilter,
        is_read: isReadFilter,
        from_date: dateFilter?.[0] || undefined,
        to_date: dateFilter?.[1] || undefined,
        created_by: createdByFilter?.value,
      },
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    );

  const notifications = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenDistributionModal = () => {
    openModal<ConfirmModalProps>(ConfirmModal, {
      onConfirm: handleMarkNotificationsAsRead,
      title: <span>Are you sure you want to mark all event logs as read?</span>,
    });
  };

  const handleMarkNotificationsAsRead = async (): Promise<void> => {
    await mutateAsync({ ids: [], read_all: true });
  };

  const { mutateAsync } = useIssuerMakeIsReadNotificationsMutation();

  const headerAction = (
    <div className="flex flex-1 flex-wrap justify-end gap-3">
      <Disclosure.Button>
        <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
          <img alt="filters" src={FiltersIcon} />
          <span className="text-sm">Filters</span>
        </div>
      </Disclosure.Button>
      <Button
        icon={CheckIcon}
        variant="secondary"
        className="w-[120px]"
        onClick={handleOpenDistributionModal}
      >
        Read all
      </Button>
    </div>
  );
  return (
    <PageLayout
      title="Event logs"
      headerAction={headerAction}
      HeaderWrapper={Disclosure}
      headerBottomContent={
        <NotificationsFilters
          filters={{
            type: typeFilter,
            dates: dateFilter,
            isRead: isReadFilter,
            createdBy: createdByFilter,
          }}
          onChangeType={setTypeFilter}
          onChangeIsRead={setIsReadFilter}
          onChangeDates={setDateFilter}
          onChangeCreatedBy={setCreatedByFilter}
        />
      }
    >
      <EmptyListState
        title="No event logs yet"
        description="Event logs about actions happening in your registry will appear here"
        isLoading={isLoading && !isFetchingNextPage}
        isSearchUsed={isSearchUsed}
        itemsCount={notifications.length}
      />

      {isLoading && !isFetchingNextPage && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {(!isLoading || isFetchingNextPage) && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={notifications.length}
          hasMore={!!hasNextPage}
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          <div className="w-full flex flex-col space-y-2">
            {notifications.map((notification, i) => (
              <IssuerNotificationItem
                key={`notification_${notification.id}_${i}`}
                notification={notification}
              />
            ))}
          </div>
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(NotificationsPage, [
  Role.SR_INVESTOR,
  Role.SR_ISSUER,
  Role.SR_ISSUER_ADMIN,
]);
